import React from 'react';
import Button from '../../../components/ui/Button';
import { CheckCircle, ArrowRight, Users, CreditCard, Shield, Star } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const OnboardingComplete = ({ data, userProfile }) => {
  const handleGetStarted = () => {
    // Navigate to main dashboard
    window.location.href = '/';
  };

  const nextSteps = [
    {
      icon: Users,
      title: 'Explore Features',
      description: 'Discover all the tools and features available in your plan',
      action: 'Browse Features',
    },
    {
      icon: CreditCard,
      title: 'Set Up Billing',
      description: 'Complete your payment setup to unlock all features',
      action: 'Configure Billing',
    },
    {
      icon: Shield,
      title: 'Security Settings',
      description: 'Configure additional security settings for your account',
      action: 'Security Center',
    },
    {
      icon: Star,
      title: 'Get Support',
      description: 'Access our help center or contact support if needed',
      action: 'Help Center',
    },
  ];

  return (
    <div className="text-center space-y-6">
      <div className="mb-8">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Welcome aboard, {userProfile?.full_name}! 🎉
        </h1>
        <p className="text-lg text-gray-600">
          Your account has been successfully set up and is ready to use.
        </p>
      </div>

      {/* Summary */}
      <div className="bg-green-50 rounded-lg p-6 mb-8">
        <h3 className="font-medium text-green-900 mb-4">Setup Complete!</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center text-green-800">
            <CheckCircle className="w-4 h-4 mr-2" />
            Profile configured
          </div>
          <div className="flex items-center text-green-800">
            <CheckCircle className="w-4 h-4 mr-2" />
            Billing information saved
          </div>
          <div className="flex items-center text-green-800">
            <CheckCircle className="w-4 h-4 mr-2" />
            Documents uploaded
          </div>
          <div className="flex items-center text-green-800">
            <CheckCircle className="w-4 h-4 mr-2" />
            Verification submitted
          </div>
        </div>
      </div>

      {/* What's Next */}
      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-gray-900">What's Next?</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          {nextSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={index} className="border border-gray-200 rounded-lg p-4 text-left hover:border-blue-300 transition-colors">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">{step.title}</h4>
                    <p className="text-sm text-gray-600 mb-3">{step.description}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => console.log(`Navigate to ${step.action}`)}
                    >
                      {step.action}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Account Status */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h4 className="font-medium text-blue-900 mb-2">Account Status</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-blue-800">Plan:</span>
            <span className="font-medium text-blue-900">
              {data?.selected_plan?.charAt(0)?.toUpperCase() + data?.selected_plan?.slice(1) || 'Basic'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-blue-800">Status:</span>
            <span className="font-medium text-blue-900">Active</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-blue-800">Verification:</span>
            <span className="font-medium text-orange-600">Pending Review</span>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="space-y-4 pt-6">
        <Button
          onClick={handleGetStarted}
          className="px-8 py-3 text-lg"
        >
          Get Started
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        
        <p className="text-sm text-gray-500">
          You can always access your account settings and billing information from the dashboard
        </p>
      </div>

      {/* Support */}
      <div className="border-t pt-6 mt-8">
        <p className="text-sm text-gray-600 mb-4">
          Need help getting started? Our support team is here to help!
        </p>
        <div className="flex justify-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open('mailto:support@example.com')}
          >
            Email Support
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open('/help', '_blank')}
          >
            Help Center
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingComplete;